"use client";

import { Box, Container, Divider, List, Stack, Text, Title } from "@mantine/core";
import { ProtectedEmailLink } from "@/app/lib/components/ProtectedEmail";
import { CONTACT_EMAIL_BASE64 } from "@/app/lib/seo/siteConfig";

export default function PrivacyPage() {
  return (
    <Container size="md" py="xl">
      <Stack gap="lg" px={{ base: "md", md: 0 }}>
        <Box mb="xl">
          <Title order={1} mb="xs">
            Privacy Policy
          </Title>

          <Text size="lg" c="gray.7">
            Last Updated: June 26, 2026
          </Text>
        </Box>

        <Text>
          At Orgtools, we take your privacy seriously. This Privacy Policy explains how we collect,
          use, disclose, and safeguard your information when you use our B2B SaaS platform. Please
          read this privacy policy carefully. If you do not agree with the terms of this privacy
          policy, please do not access our platform.
        </Text>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Information We Collect
          </Title>
          <Text mb="xs">We collect information that you provide directly to us when you:</Text>
          <List withPadding>
            <List.Item>Register for an account</List.Item>
            <List.Item>Use our services</List.Item>
            <List.Item>Communicate with our support team</List.Item>
            <List.Item>Respond to surveys or questionnaires</List.Item>
          </List>

          <Text mt="md">
            This information may include your name, email address, company name, job title, billing
            information, and any other information you choose to provide.
          </Text>

          <Text mt="md">We also collect:</Text>
          <List withPadding>
            <List.Item>Usage data and analytics (pages visited, features used)</List.Item>
            <List.Item>
              Integration-related data (meeting recordings, transcripts, documents)
            </List.Item>
            <List.Item>Technical data (IP addresses, browser type, device information)</List.Item>
            <List.Item>Communication data (emails, support tickets, feedback)</List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Legal Basis for Processing
          </Title>
          <Text>
            We process your personal information based on: (1) contract performance when you use our
            services, (2) legitimate interests in providing and improving our platform and ensuring
            the safety and integrity of our AI features, (3) consent for optional features like
            integrations, and (4) legal obligations where required by law.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            How We Use Your Information
          </Title>
          <Text mb="xs">We use the information we collect to:</Text>
          <List withPadding>
            <List.Item>Provide, maintain, and improve our services</List.Item>
            <List.Item>Process transactions and send related information</List.Item>
            <List.Item>Send administrative messages, updates, and security alerts</List.Item>
            <List.Item>Respond to your comments, questions, and requests</List.Item>
            <List.Item>Provide customer service and technical support</List.Item>
            <List.Item>Monitor and analyze usage patterns and trends</List.Item>
            <List.Item>Process integration data and meeting information</List.Item>
            <List.Item>Generate AI-powered insights and decision summaries</List.Item>
            <List.Item>Comply with legal obligations and enforce our terms</List.Item>
            <List.Item>
              Operate and improve AI Advisors and related safety systems and content filters
            </List.Item>
            <List.Item>
              Detect, prevent, and mitigate abuse, fraud, and security incidents
            </List.Item>
            <List.Item>
              Use de-identified or aggregated data and usage metadata to improve the Service and its
              features
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Data Sharing and Disclosure
          </Title>
          <Text mb="xs">
            We do not sell your personal information to third parties. We may share your information
            with:
          </Text>
          <List withPadding>
            <List.Item>Service providers who perform services on our behalf</List.Item>
            <List.Item>Professional advisors, such as lawyers, auditors, and insurers</List.Item>
            <List.Item>Government bodies when required by law</List.Item>
            <List.Item>Business partners with your consent</List.Item>
            <List.Item>
              Integrations you choose to connect, to process the data needed for those features
            </List.Item>
          </List>
          <Text mt="md">
            AI features process your content within our cloud provider's managed environment (Amazon
            Bedrock). We do not send your content to separate third-party model-provider APIs, and
            the model providers whose models we use do not receive, retain, or train on your
            content.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            AI Processing and Model Providers
          </Title>
          <Text>
            Some features use large language models to generate insights, summaries, and decision
            support. We run these models within our cloud provider's managed environment (Amazon
            Bedrock). Your content is processed inside that environment and is not sent to separate
            third-party model-provider APIs. The model providers whose models we use within Amazon
            Bedrock do not receive your content directly, retain it, or use it to train their
            models.
          </Text>
          <Text mt="md">
            We do not use your content, prompts, or outputs to train foundation models. We maintain
            observability and tracing to operate the Service reliably. In production this telemetry
            records operational metadata, such as model, timing, and error information, and excludes
            the content of your prompts and the models' responses.
          </Text>
          <Text mt="md">
            Some agent features can perform web searches to fulfill your request. Search queries are
            sent to our web search provider. Do not submit information you are not authorized to
            process through the Service.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Data Security
          </Title>
          <Text>
            We implement appropriate technical and organizational measures to protect the security
            of your personal information. However, please be aware that no method of transmission
            over the Internet or electronic storage is 100% secure.
          </Text>
          <Text mt="md">
            In the event of a data breach that affects your personal information, we will notify you
            and relevant authorities as required by applicable law, typically within 72 hours of
            becoming aware of the breach.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Data Retention
          </Title>
          <Text>
            We retain your personal information for as long as necessary to provide our services,
            comply with legal obligations, resolve disputes, and enforce our agreements. Account
            data is retained for the duration of your account plus 30 days. Integration-related data
            (like meeting recordings) is processed and deleted within 14 days unless you choose to
            retain it. You may request deletion of your data at any time.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            International Data Transfers
          </Title>
          <Text>
            Your information may be transferred to and processed in countries other than your own.
            We ensure appropriate safeguards are in place for such transfers, including standard
            contractual clauses and adequacy decisions where applicable.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Your Rights
          </Title>
          <Text mb="xs">
            Depending on your location, you may have certain rights regarding your personal
            information, including:
          </Text>
          <List withPadding>
            <List.Item>Access to your personal information</List.Item>
            <List.Item>Correction of inaccurate information</List.Item>
            <List.Item>Deletion of your information</List.Item>
            <List.Item>Restriction of processing</List.Item>
            <List.Item>Data portability</List.Item>
          </List>
          <Text mt="md">
            To exercise these rights, please contact us at{" "}
            <ProtectedEmailLink address={CONTACT_EMAIL_BASE64.privacy} />. We will respond to your
            request within 30 days and may require verification of your identity before processing
            any requests. You also have the right to lodge a complaint with your local data
            protection authority if you believe we have not adequately addressed your concerns.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Cookies and Tracking Technologies
          </Title>
          <Text>
            We use cookies and similar tracking technologies to track activity on our platform and
            hold certain information. You can instruct your browser to refuse all cookies or to
            indicate when a cookie is being sent.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Changes to This Privacy Policy
          </Title>
          <Text>
            We may update our Privacy Policy from time to time. We will notify you of any changes by
            posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Contact Us
          </Title>
          <Text>If you have any questions about this Privacy Policy, please contact us at:</Text>
          <Text mt="xs" fw={500}>
            Orgtools
            <br />
            10768 Rocky Peak Place
            <br />
            Gig Harbor, WA 98332
            <br />
            <ProtectedEmailLink address={CONTACT_EMAIL_BASE64.privacy} />
          </Text>
        </Box>
      </Stack>
    </Container>
  );
}
